import { create } from 'zustand';

/** Гімн, що грає на сайті: один трек, по колу. Лежить у `public`. */
export const ANTHEM_SRC = '/anthem.mp3';
export const ANTHEM_TITLE = 'Гімн Держспецзвʼязку';

const KEY = 'kanc.music';

interface MusicState {
  on: boolean;
  /** Трек справді грає зараз (а не лише ввімкнено): від цього залежить вигляд сайту. */
  playing: boolean;
  /** 0…1 */
  volume: number;
  setOn: (on: boolean) => void;
  setVolume: (v: number) => void;
}

const read = (): { on: boolean; volume: number } => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<{ on: boolean; volume: number }>;
    const volume = typeof raw.volume === 'number' && raw.volume >= 0 && raw.volume <= 1 ? raw.volume : 0.6;
    return { on: raw.on === true, volume };
  } catch {
    return { on: false, volume: 0.6 };
  }
};

const save = (s: { on: boolean; volume: number }): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ on: s.on, volume: s.volume }));
  } catch {
    // Сховище закрите — налаштування діє до перезавантаження.
  }
};

let audio: HTMLAudioElement | null = null;
export const player = (): HTMLAudioElement => {
  if (!audio) {
    audio = new Audio(ANTHEM_SRC);
    audio.loop = true;
    audio.preload = 'none';
    audio.addEventListener('playing', () => {
      useMusic.setState({ playing: true });
      partyOn(audio!);
    });
    const stopped = (): void => {
      useMusic.setState({ playing: false });
      partyOff();
    };
    audio.addEventListener('pause', stopped);
    audio.addEventListener('ended', stopped);
  }
  return audio;
};

/**
 * Ритм: аналізатор слухає низи треку, і на кожен удар сайт спалахує то синім,
 * то жовтим, а вікна підстрибують. Живе лише поки грає музика. Мигання
 * приглушене й вимикається, якщо в системі просять менше руху.
 */
let analyser: AnalyserNode | null = null;
let audioCtx: AudioContext | null = null;
let bins: Uint8Array<ArrayBuffer> | null = null;
let raf = 0;
let flash: HTMLDivElement | null = null;
/** Сила останнього удару 0…1 — летючі емблеми теж пульсують у ритм. */
export let beatLevel = 0;

const reduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function setupAnalyser(a: HTMLAudioElement): void {
  if (analyser) return;
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    audioCtx = ctx;
    const src = ctx.createMediaElementSource(a);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.2;
    src.connect(analyser);
    analyser.connect(ctx.destination);
    bins = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
  } catch {
    analyser = null; // Без аналізатора музика все одно грає, просто без ритму.
  }
}

function beatLoop(): void {
  const root = document.documentElement;
  if (!analyser || !bins) return;
  const nyquist = (audioCtx?.sampleRate ?? 44100) / 2;
  const binHz = nyquist / bins.length;
  // Бочка живе у 40–150 Гц: беремо лише цей смуг, а не всі низи — інакше удар тоне в басовій лінії.
  const lo = Math.max(1, Math.floor(40 / binHz));
  const hi = Math.max(lo + 1, Math.ceil(150 / binHz));
  let prev = 0;
  let mean = 0.05; // середній стрибок енергії
  let dev = 0.05; // його розкид
  let beat = 0;
  let last = 0;
  let side = false;
  const tick = (now: number): void => {
    raf = requestAnimationFrame(tick);
    analyser!.getByteFrequencyData(bins!);
    let e = 0;
    for (let i = lo; i <= hi; i++) e += bins![i]!;
    e /= (hi - lo + 1) * 255;
    // Стрибок енергії, а не її рівень: гучний бас без ударів не мигає, а тихий удар на тлі тиші — так.
    const flux = Math.max(0, e - prev);
    prev = e;
    const limit = mean + dev * 1.1 + 0.015;
    if (flux > limit && e > 0.18 && now - last > 170) {
      last = now;
      beat = Math.min(1, 0.6 + (flux - mean) * 4);
      side = !side;
      root.dataset.beat = side ? 'blue' : 'yellow';
    }
    mean = mean * 0.95 + flux * 0.05;
    dev = dev * 0.95 + Math.abs(flux - mean) * 0.05;
    beat *= 0.84;
    beatLevel = beat;
    root.style.setProperty('--beat', beat.toFixed(3));
  };
  raf = requestAnimationFrame(tick);
}

function partyOn(a: HTMLAudioElement): void {
  if (reduced()) return;
  setupAnalyser(a);
  if (!analyser) return;
  void audioCtx?.resume();
  const root = document.documentElement;
  if (!flash) {
    flash = document.createElement('div');
    flash.id = 'beat-flash';
    flash.setAttribute('aria-hidden', 'true');
    document.body.appendChild(flash);
  }
  root.dataset.party = '';
  cancelAnimationFrame(raf);
  beatLoop();
}

function partyOff(): void {
  cancelAnimationFrame(raf);
  const root = document.documentElement;
  delete root.dataset.party;
  delete root.dataset.beat;
  root.style.removeProperty('--beat');
  beatLevel = 0;
}

/**
 * Браузер не дає грати без жесту користувача. Клік по перемикачу — жест, а от
 * після перезавантаження сторінки «увімкнено» лишається без жесту: тоді чекаємо
 * першого дотику чи клавіші на сторінці й стартуємо тоді.
 */
let waiting = false;
const start = (): void => {
  const a = player();
  a.volume = useMusic.getState().volume;
  void a.play().catch(() => {
    if (waiting) return;
    waiting = true;
    const retry = (): void => {
      window.removeEventListener('pointerdown', retry, true);
      window.removeEventListener('keydown', retry, true);
      waiting = false;
      if (useMusic.getState().on) void player().play().catch(() => undefined);
    };
    window.addEventListener('pointerdown', retry, true);
    window.addEventListener('keydown', retry, true);
  });
};

const initial = read();

export const useMusic = create<MusicState>((set, get) => ({
  ...initial,
  playing: false,
  setOn: on => {
    set({ on });
    save(get());
    if (on) start();
    else audio?.pause();
  },
  setVolume: volume => {
    set({ volume });
    save(get());
    if (audio) audio.volume = volume;
  },
}));

/** Запускає музику, якщо вона була ввімкнена до перезавантаження. */
export function initMusic(): void {
  if (useMusic.getState().on) start();
}
